import React, { useEffect, useId, useRef, useState } from "react";
import styles from "./Accordion.module.css";

export function Accordion({
  icon = null,
  items,
  allowMultipleOpen = true,
  defaultOpenIndexes = [],
}) {
  const [open, setOpen] = useState(() =>
    items.map((_, i) => defaultOpenIndexes.includes(i))
  );

  const toggle = (index) => {
    setOpen((prev) => {
      if (allowMultipleOpen) {
        const next = [...prev];
        next[index] = !next[index];
        return next;
      }

      return prev.map((_, i) => (i === index ? !prev[i] : false));
    });
  };

  return (
    <div className={styles.accList}>
      {items.map((item, i) => (
        <AccordionItem
          key={i}
          title={item.title}
          icon={item.icon}
          isOpen={open[i]}
          onToggle={() => toggle(i)}
        >
          {item.content}
        </AccordionItem>
      ))}
    </div>
  );
}

function AccordionItem({ title, icon, isOpen, onToggle, children }) {
  const contentId = useId();
  const innerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const measure = () => {
      setMaxHeight(el.scrollHeight);
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    return () => ro.disconnect();
  }, [children]);

  return (
    <div className={`${styles.accItem} ${isOpen ? styles.isOpen : ""}`}>
      <button
        type="button"
        className={styles.accHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        {icon && (
          <div className={styles.accIconContainer}>
            <img src={icon} alt="" className={styles.accIcon} />
          </div>
        )}
        <span className={`${styles.accTitle} ${isOpen ? styles.isOpen : ""}`}>{title}</span>

        <span className={`${styles.accChevron} ${isOpen ? "" : styles.rotated}`} aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 14l6-6 6 6"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div
        id={contentId}
        className={styles.accPanel}
        style={{ maxHeight: isOpen ? maxHeight : 0 }}
      >
        <div ref={innerRef} className={styles.accInner}>
          {children}
        </div>
      </div>
    </div>
  );
}
